import { Tag, Typography } from "antd";
import type { TableProps } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { compareNumeric, compareText, toBoolean } from "../api/format";
import type {
  CircumscriptionContactRow,
  CongregationContactRow,
  CoordinatorRow,
  SpeakerRow,
  SpeechRow,
} from "../api/types";
import type { ExportableColumn } from "./DataTable";

/**
 * Speeches and speakers are two views of the same relation: the speech list
 * drills down into its speakers, the speaker list into its speeches. Both
 * tables therefore appear twice, and the column definitions live here so the
 * two places cannot drift apart.
 *
 * The labels are the Rails ones (activerecord.attributes.*) — the same strings
 * the old application showed, already translated into all three languages.
 */
export function useSpeechColumns(): TableProps<SpeechRow>["columns"] {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        title: t("activerecord.attributes.speech.schema"),
        dataIndex: "schema",
        width: 100,
        // The schema is a number kept in a string column — the server orders by
        // `schema * 1` for the same reason.
        sorter: (a, b) => compareNumeric(a.schema, b.schema),
        defaultSortOrder: "ascend",
      },
      {
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "title",
        ellipsis: true,
        sorter: (a, b) => compareText(a.title, b.title),
      },
      {
        title: t("activerecord.attributes.speech.language_id"),
        dataIndex: "language_code",
        width: 110,
        sorter: (a, b) => compareText(a.language_code, b.language_code),
      },
      {
        title: t("activerecord.attributes.speech.speakers"),
        dataIndex: "speaker_count",
        width: 110,
        align: "right",
        sorter: (a, b) => a.speaker_count - b.speaker_count,
      },
    ],
    [t],
  );
}

export function useSpeakerColumns(): ExportableColumn<SpeakerRow>[] {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        title: t("activerecord.attributes.speaker.first_name"),
        dataIndex: "first_name",
        width: 150,
        sorter: (a, b) => compareText(a.first_name, b.first_name),
        defaultSortOrder: "ascend",
      },
      {
        title: t("activerecord.attributes.speaker.last_name"),
        dataIndex: "last_name",
        width: 150,
        sorter: (a, b) => compareText(a.last_name, b.last_name),
      },
      {
        title: t("activerecord.attributes.circumscription.short"),
        dataIndex: "circumscription_code",
        width: 90,
        sorter: (a, b) => compareText(a.circumscription_code, b.circumscription_code),
      },
      {
        title: t("activerecord.models.congregation"),
        dataIndex: "congregation",
        width: 180,
        ellipsis: true,
        sorter: (a, b) => compareText(a.congregation, b.congregation),
      },
      {
        title: t("activerecord.attributes.speaker.external"),
        dataIndex: "external",
        width: 90,
        render: (value: SpeakerRow["external"]) =>
          toBoolean(value) ? <Tag>{t("activerecord.attributes.speaker.external")}</Tag> : null,
        // The API answers 0/1 here; without this the export writes the number.
        exportValue: (row) => toBoolean(row.external),
      },
      {
        title: t("activerecord.attributes.speaker.speeches"),
        dataIndex: "speech_count",
        width: 110,
        align: "right",
        sorter: (a, b) => a.speech_count - b.speech_count,
      },
      {
        title: t("activerecord.attributes.user.zip_code"),
        dataIndex: "zip_code",
        width: 90,
      },
      {
        title: t("activerecord.attributes.user.country"),
        dataIndex: "country",
        width: 90,
      },
      {
        title: t("activerecord.attributes.user.phone"),
        dataIndex: "phone",
        width: 150,
        render: (value: string | null) => <ContactLink href={value ? `tel:${value}` : null} text={value} />,
      },
      {
        title: t("activerecord.attributes.user.mobile"),
        dataIndex: "mobile",
        width: 150,
        render: (value: string | null) => <ContactLink href={value ? `tel:${value}` : null} text={value} />,
      },
      {
        title: t("activerecord.attributes.user.private_email"),
        dataIndex: "email",
        width: 220,
        ellipsis: true,
        render: (value: string | null) => (
          <ContactLink href={value ? `mailto:${value}` : null} text={value} />
        ),
      },
    ],
    [t],
  );
}

/**
 * The two contact lists. Both drill down into the coordinators of the row, so
 * the same three tables appear across two screens — which is why they live
 * here rather than in the pages.
 */
export function useCircumscriptionContactColumns(): TableProps<CircumscriptionContactRow>["columns"] {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        title: t("activerecord.attributes.circumscription.circumscription_code"),
        dataIndex: "circumscription_code",
        width: 90,
        sorter: (a, b) => compareText(a.circumscription_code, b.circumscription_code),
        defaultSortOrder: "ascend",
      },
      {
        title: t("activerecord.attributes.circumscription.denomination"),
        dataIndex: "denomination",
        ellipsis: true,
        sorter: (a, b) => compareText(a.denomination, b.denomination),
      },
      {
        title: t("activerecord.attributes.language.language_code"),
        dataIndex: "language_code",
        width: 90,
      },
      {
        title: t("activerecord.attributes.circumscription.country"),
        dataIndex: "country",
        width: 90,
      },
      ...contactColumns<CircumscriptionContactRow>(t),
    ],
    [t],
  );
}

export function useCongregationContactColumns(): TableProps<CongregationContactRow>["columns"] {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        title: t("activerecord.attributes.circumscription.short"),
        dataIndex: "circumscription_code",
        width: 90,
        sorter: (a, b) => compareText(a.circumscription_code, b.circumscription_code),
        defaultSortOrder: "ascend",
      },
      {
        title: t("activerecord.attributes.congregation.congregation_code"),
        dataIndex: "congregation_code",
        width: 90,
        sorter: (a, b) => compareText(a.congregation_code, b.congregation_code),
      },
      {
        title: t("activerecord.attributes.congregation.denomination"),
        dataIndex: "denomination",
        ellipsis: true,
        sorter: (a, b) => compareText(a.denomination, b.denomination),
      },
      {
        title: t("activerecord.attributes.language.language_code"),
        dataIndex: "language_code",
        width: 90,
      },
      {
        title: t("activerecord.attributes.congregation.state"),
        dataIndex: "state",
        width: 110,
      },
      {
        title: t("activerecord.attributes.congregation.country"),
        dataIndex: "country",
        width: 90,
      },
      {
        title: t("activerecord.attributes.congregation.congregation_phone"),
        dataIndex: "congregation_phone",
        width: 150,
        render: (value: string | null) => (
          <ContactLink href={value ? `tel:${value}` : null} text={value} />
        ),
      },
      ...contactColumns<CongregationContactRow>(t),
    ],
    [t],
  );
}

/** The coordinators behind a contact row — same shape on both screens. */
export function useCoordinatorColumns(): TableProps<CoordinatorRow>["columns"] {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        title: t("activerecord.attributes.local_coordinator.first_name"),
        dataIndex: "first_name",
        sorter: (a, b) => compareText(a.first_name, b.first_name),
        defaultSortOrder: "ascend",
      },
      {
        title: t("activerecord.attributes.local_coordinator.last_name"),
        dataIndex: "last_name",
        sorter: (a, b) => compareText(a.last_name, b.last_name),
      },
      {
        title: t("activerecord.attributes.user.phone"),
        dataIndex: "phone",
        render: (value: string | null) => (
          <ContactLink href={value ? `tel:${value}` : null} text={value} />
        ),
      },
      {
        title: t("activerecord.attributes.user.mobile"),
        dataIndex: "mobile",
        render: (value: string | null) => (
          <ContactLink href={value ? `tel:${value}` : null} text={value} />
        ),
      },
      {
        title: t("activerecord.attributes.user.private_email"),
        dataIndex: "private_email",
        ellipsis: true,
        render: (value: string | null) => (
          <ContactLink href={value ? `mailto:${value}` : null} text={value} />
        ),
      },
    ],
    [t],
  );
}

/** The fields a row needs to carry a named contact person. */
interface WithContactPerson {
  contact_first_name: string | null;
  contact_last_name: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
}

/**
 * The named contact person, identical on both lists. The return type is
 * deliberately non-optional: `TableProps["columns"]` includes undefined, and
 * spreading that would strip the contextual typing from the array it lands in —
 * every `sorter` in the surrounding list would silently fall back to `any`.
 */
function contactColumns<T extends WithContactPerson>(
  t: ReturnType<typeof useTranslation>["t"],
): NonNullable<TableProps<T>["columns"]> {
  return [
    {
      title: t("activerecord.attributes.circumscription.contact_first_name"),
      dataIndex: "contact_first_name",
      width: 140,
    },
    {
      title: t("activerecord.attributes.circumscription.contact_last_name"),
      dataIndex: "contact_last_name",
      width: 140,
    },
    {
      title: t("activerecord.attributes.circumscription.phone"),
      dataIndex: "phone",
      width: 150,
      render: (value: string | null) => (
        <ContactLink href={value ? `tel:${value}` : null} text={value} />
      ),
    },
    {
      title: t("activerecord.attributes.circumscription.mobile"),
      dataIndex: "mobile",
      width: 150,
      render: (value: string | null) => (
        <ContactLink href={value ? `tel:${value}` : null} text={value} />
      ),
    },
    {
      title: t("activerecord.attributes.circumscription.email"),
      dataIndex: "email",
      width: 220,
      ellipsis: true,
      render: (value: string | null) => (
        <ContactLink href={value ? `mailto:${value}` : null} text={value} />
      ),
    },
  ];
}

/** stopPropagation, because the surrounding row opens the drill-down drawer. */
function ContactLink({ href, text }: { href: string | null; text: string | null }) {
  if (!href || !text) return null;

  return (
    <Typography.Link href={href} onClick={(event) => event.stopPropagation()}>
      {text}
    </Typography.Link>
  );
}
